import { cx } from '@flowus/common/cx';
import { globalListenerHelper } from '@flowus/common/hooks/global-listener-helper';
import { deepEqual } from '@flowus/common/utils/tools';
import { useLocalStorageState } from 'ahooks';
import isHotkey from 'is-hotkey';
import type { FC, MouseEvent, ReactNode, RefObject } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { TransitionBox } from 'src/common/components/transition-box';
import { observerDomSize } from 'src/common/utils/use-size';
import { LocalStorageKey } from 'src/const/local-storage-state-key';
import { ID_TOC_CONTAINER } from 'src/hooks/page/use-dnd/helper';
import { useIsDragging } from 'src/hooks/page/use-dnd/hooks';
import { $appUiStateCache, useIsCollapse } from 'src/services/app/hook';
import { $searchParams } from 'src/utils';
import { judgeSharePage } from 'src/utils/getPageId';
import { AiHistoryList } from './aside/ai-history-list';
import { useToggleCollapse } from './aside/toc-trigger';

interface Props {
  aside: ReactNode;
}

export const defaultAsideWidth = 200;
export const Layout: FC<Props> = memo(({ aside, children }) => {
  // const isMobileSize = useIsMobileSize();
  const isDragging = useIsDragging();
  const isCollapsed = useIsCollapse();
  const asideRef = useRef<HTMLDivElement>(null);
  const [asideWidth, setAsideWidth] = useLocalStorageState(LocalStorageKey.MAIN_LEFT_WIDTH, {
    defaultValue: defaultAsideWidth,
  });
  const [resizeAside, setResizeAside] = useState(false);
  const leftWidth = Math.min(Math.max(200, asideWidth ?? 0), window.innerWidth * 0.7);
  const [isHoverShowToc, setIsHoverShowToc] = useState(false);
  const hoverTimer = useRef<number>();
  const mainRef = useRef<HTMLElement>(null);
  const toggleCollapse = useToggleCollapse();

  useEffect(() => {
    if (self === parent) return;
    if (!mainRef.current) return;
    observerDomSize(mainRef.current, ({ height }) => {
      parent.postMessage({ height, from: 'buildin' }, '*');
    });
  }, []);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (isHotkey(['mod+\\', 'mod+、'])(event)) {
        event.preventDefault();
        toggleCollapse(!$appUiStateCache.$isCollapsed);
      }
    };
    globalListenerHelper.addEventListener('keydown', handleKeydown);

    return () => {
      globalListenerHelper.removeEventListener('keydown', handleKeydown);
    };
  }, [aside, toggleCollapse]);

  useEffect(() => {
    const root = document.getElementById('root');
    const asideToc = document.getElementById(ID_TOC_CONTAINER);
    if (isDragging) {
      root?.classList.add('dragging');
      // 拖拽时目录时，防止释放在自身上会触发点击跳转
      asideToc?.classList.add('pointer-events-none');
    } else {
      root?.classList.remove('dragging');
      asideToc?.classList.remove('pointer-events-none');
    }
  }, [isDragging]);

  // 自动收起侧边栏 // 不好用
  // useEffect(() => {
  //   toggleCollapse(isMobileSize || localStorage.getItem(TOC_COLLAPSED) === 'true');
  // }, [isMobileSize, toggleCollapse]);

  useEffect(() => {
    if (!aside && !judgeSharePage()) {
      toggleCollapse(true);
    }
  }, [aside, toggleCollapse]);

  useEffect(() => {
    const leave = () => {
      hoverTimer.current && clearTimeout(hoverTimer.current);
      setIsHoverShowToc(false);
    };

    document.body.addEventListener('mouseleave', leave);

    return () => {
      document.body.removeEventListener('mouseleave', leave);
    };
  }, []);

  const handleMouseDown = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setResizeAside(true);
    const mouseMove = (event: globalThis.MouseEvent) => setAsideWidth(event.pageX);
    window.addEventListener(
      'mouseup',
      () => {
        window.removeEventListener('mousemove', mouseMove);
        setResizeAside(false);
      },
      { once: true }
    );
    window.addEventListener('mousemove', mouseMove);
  };

  const handleMousemove = useCallback(
    (event) => {
      hoverTimer.current && clearTimeout(hoverTimer.current);
      if (
        (!isHoverShowToc && isCollapsed && event.clientX < 15) ||
        (isHoverShowToc && !asideRef.current?.contains(event.target) && event.clientX > 15)
      ) {
        const status = !isHoverShowToc;
        // 隐藏
        hoverTimer.current = window.setTimeout(
          () => {
            setIsHoverShowToc(!isHoverShowToc);
          },
          status ? 100 : 0
        );
      }
    },
    [isCollapsed, isHoverShowToc]
  );

  const isHiddenAside = !(isCollapsed && !isHoverShowToc);
  const translateX = !isHiddenAside ? `-${leftWidth}px` : '0px';

  const asideStyle = {
    width: leftWidth,
    transform: `translateX(${translateX})`,
    borderRadius: isCollapsed ? '0px 4px 4px 0px' : '0px 0px 0px 0px',
    top: isCollapsed ? '70px' : '0px',
    height: `calc(100vh - ${isCollapsed ? '140px' : '0px'})`,
    opacity: isHiddenAside ? 1 : 0,
    boxShadow:
      isHiddenAside && isHoverShowToc
        ? 'rgba(0, 0, 0, 0.03) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 3px 6px, rgba(0, 0, 0, 0.1) 0px 12px 20px'
        : 'rgba(0, 0, 0, 0.0) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 3px 6px,rgba(0, 0, 0, 0) 0px 12px 20px',
  };

  const mainPaddingLeft = isCollapsed || !aside ? 0 : leftWidth;

  return (
    <>
      {aside && (
        <>
          <div
            className={
              'inset-y-0 left-0 z-[100] transition-all duration-200 bg-aside aside max-h-min transform-gpu fixed'
            }
            ref={asideRef}
            style={asideStyle}
          >
            {aside}
            {!isCollapsed && (
              <TransitionBox
                className={cx(
                  'fixed inset-y-0 w-px z-[100] animate-hover !cursor-col-resize bg-black_006 hover:w-0.5',
                  resizeAside && 'w-0.5'
                )}
                style={{ left: mainPaddingLeft }}
                onMouseDown={handleMouseDown}
                onDoubleClick={() => {
                  setAsideWidth(defaultAsideWidth);
                }}
              />
            )}
          </div>
          <AiHistoryList
            style={{ left: isCollapsed ? 0 : leftWidth, animationDuration: '200ms' }}
          />
        </>
      )}
      <TransitionBox
        tagName="main"
        className={cx('bg-white1 overflow-y-auto', {
          'h-auto': $searchParams.print,
          'flex flex-col min-h-full': $searchParams.blog,
          '!bg-[#0d0c0f]': $searchParams.blog === 'walles',
          'flex flex-col h-full': !$searchParams.print && !$searchParams.blog,
        })}
        domRef={mainRef as RefObject<HTMLDivElement>}
        style={{ paddingLeft: $searchParams.print ? 0 : mainPaddingLeft }}
        onMouseMove={handleMousemove}
      >
        {children}
      </TransitionBox>
    </>
  );
}, deepEqual);
